import { useRef, useCallback, useEffect } from 'react';
import { PixiApp } from '../../pixi/PixiApp';
import {
  useEditorStore,
  type PaletteEntityPayload,
  type PaletteLayoutPayload,
} from '../../stores/editorStore';

type PaletteDropPayload =
  | { kind: 'entity'; data: PaletteEntityPayload }
  | { kind: 'layout'; data: PaletteLayoutPayload };

declare global {
  interface Window {
    __imbasionEditorDropFromPalette?: (payload: unknown, clientX: number, clientY: number) => void;
  }
}

function isPaletteDropPayload(payload: unknown): payload is PaletteDropPayload {
  if (!payload || typeof payload !== 'object') return false;

  const value = payload as { kind?: unknown; data?: unknown };
  return (
    (value.kind === 'entity' || value.kind === 'layout') &&
    !!value.data &&
    typeof value.data === 'object'
  );
}

export default function EditorCanvas() {
  const containerRef = useRef<HTMLDivElement>(null);
  const pixiAppRef = useRef<PixiApp | null>(null);

  const {
    levelData,
    objects,
    entities,
    mode,
    selectedIds,
    layerVisibility,
    entityLayerAssignments,
    addEntityFromPalette,
    addLayoutFromPalette,
  } = useEditorStore();

  // Initialize Pixi app
  useEffect(() => {
    if (!containerRef.current) return;

    const pixiApp = new PixiApp(containerRef.current);
    pixiAppRef.current = pixiApp;

    return () => {
      pixiApp.destroy();
      pixiAppRef.current = null;
    };
  }, []);

  // Update level data
  useEffect(() => {
    if (!pixiAppRef.current || !levelData) return;
    pixiAppRef.current.setLevelData(levelData);
  }, [levelData]);

  // Update objects/entities when they change
  useEffect(() => {
    if (!pixiAppRef.current) return;
    try {
      pixiAppRef.current.updateObjects(objects, entities, mode);
    } catch (err) {
      console.error('[EditorCanvas] Error en updateObjects:', err);
    }
  }, [objects, entities, mode, layerVisibility, entityLayerAssignments]);

  // Update selection
  useEffect(() => {
    if (!pixiAppRef.current) return;
    pixiAppRef.current.setSelection(selectedIds);
  }, [selectedIds]);

  // Update layer visibility
  useEffect(() => {
    if (!pixiAppRef.current) return;
    pixiAppRef.current.setLayerVisibility(layerVisibility);
  }, [layerVisibility, entityLayerAssignments]);

  const addPaletteItemAtClientPoint = useCallback(
    (payload: PaletteDropPayload, clientX: number, clientY: number) => {
      if (!pixiAppRef.current || !levelData) return;

      const world = pixiAppRef.current.screenToWorldPoint(clientX, clientY);

      if (payload.kind === 'entity') {
        addEntityFromPalette(payload.data, world.x, world.y);
      } else {
        addLayoutFromPalette(payload.data, world.x, world.y);
      }

      window.requestAnimationFrame(() => {
        if (!pixiAppRef.current) return;
        const state = useEditorStore.getState();
        pixiAppRef.current.updateObjects(state.objects, state.entities, state.mode);
        pixiAppRef.current.setSelection(state.selectedIds);
        pixiAppRef.current.setLayerVisibility(state.layerVisibility);
      });
    },
    [addEntityFromPalette, addLayoutFromPalette, levelData]
  );

  useEffect(() => {
    window.__imbasionEditorDropFromPalette = (payload, clientX, clientY) => {
      if (!isPaletteDropPayload(payload)) return;
      addPaletteItemAtClientPoint(payload, clientX, clientY);
    };

    return () => {
      if (window.__imbasionEditorDropFromPalette) {
        delete window.__imbasionEditorDropFromPalette;
      }
    };
  }, [addPaletteItemAtClientPoint]);

  return (
    <div ref={containerRef} className="editor-canvas" style={{ width: '100%', height: '100%' }} />
  );
}
